import { Skeleton, Tooltip } from '@affine/component';
import { AiIcon, CloseIcon, GroupIcon } from '@blocksuite/icons/rc';
import {
  type CSSProperties,
  type MouseEventHandler,
  type ReactNode,
  useCallback,
} from 'react';

import { CachedUserAvatar } from '../cached-user-avatar';
import * as styles from './styles.css';

export type IdentityRowVariant = 'row' | 'option' | 'compact' | 'pill';

export interface IdentityRowProps {
  variant?: IdentityRowVariant;
  title: ReactNode;
  subtitle?: ReactNode;
  avatar?: ReactNode;
  icon?: ReactNode;
  badges?: ReactNode;
  actions?: ReactNode;
  onRemove?: () => void;
  removeDisabled?: boolean;
  removeTestId?: string;
  removeIcon?: ReactNode;
  focused?: boolean;
  loading?: boolean;
  maxWidth?: number | string;
  className?: string;
  style?: CSSProperties;
  dataTestId?: string;
}

export interface PersonIdentity {
  id?: string;
  name?: string | null;
  email?: string | null;
  avatarUrl?: string | null;
}

export interface PersonRowProps extends Omit<
  IdentityRowProps,
  'avatar' | 'icon' | 'title' | 'subtitle'
> {
  person: PersonIdentity;
  fallbackName?: string;
  avatarSize?: number;
  subtitle?: ReactNode;
  showTooltips?: boolean;
}

export interface GroupRowProps extends Omit<
  IdentityRowProps,
  'avatar' | 'icon' | 'title' | 'subtitle'
> {
  name: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  showTooltips?: boolean;
}

export function IdentityRow({
  variant = 'row',
  title,
  subtitle,
  avatar,
  icon,
  badges,
  actions,
  onRemove,
  removeDisabled = false,
  removeTestId,
  removeIcon = <CloseIcon />,
  focused = false,
  loading = false,
  maxWidth,
  className,
  style,
  dataTestId,
}: IdentityRowProps) {
  const handleRemove: MouseEventHandler<HTMLButtonElement> = useCallback(
    event => {
      event.stopPropagation();
      onRemove?.();
    },
    [onRemove]
  );

  return (
    <div
      className={className ? `${styles.row} ${className}` : styles.row}
      data-variant={variant}
      data-focused={focused}
      data-testid={dataTestId}
      style={{ ...style, maxWidth: maxWidth ?? style?.maxWidth }}
    >
      <div className={styles.main}>
        {avatar || icon ? (
          <div className={styles.avatar}>{avatar ?? icon}</div>
        ) : null}
        <div className={styles.details}>
          <div className={styles.titleLine}>
            <div className={styles.title}>
              {loading ? <Skeleton width="8em" height="14px" /> : title}
            </div>
            {badges ? <div className={styles.badges}>{badges}</div> : null}
          </div>
          {variant !== 'pill' && subtitle ? (
            <div className={styles.subtitle}>
              {loading ? <Skeleton width="12em" height="12px" /> : subtitle}
            </div>
          ) : null}
        </div>
      </div>
      {actions || onRemove ? (
        <div className={styles.actions}>
          {actions}
          {onRemove ? (
            <button
              type="button"
              data-testid={removeTestId}
              className={styles.removeButton}
              disabled={removeDisabled}
              onClick={handleRemove}
            >
              {removeIcon}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function PersonRow({
  person,
  fallbackName = 'User',
  avatarSize,
  subtitle,
  showTooltips = false,
  loading = false,
  variant = 'row',
  ...rowProps
}: PersonRowProps) {
  const displayName = person.name || person.email || fallbackName;
  const displaySubtitle = subtitle ?? (person.name ? person.email : undefined);
  const size =
    avatarSize ?? (variant === 'pill' ? 16 : variant === 'compact' ? 28 : 36);
  const title = showTooltips ? (
    <Tooltip content={displayName} rootOptions={{ delayDuration: 1000 }}>
      <span>{displayName}</span>
    </Tooltip>
  ) : (
    displayName
  );
  const subtitleContent =
    showTooltips && displaySubtitle ? (
      <Tooltip content={displaySubtitle} rootOptions={{ delayDuration: 1000 }}>
        <span>{displaySubtitle}</span>
      </Tooltip>
    ) : (
      displaySubtitle
    );

  return (
    <IdentityRow
      {...rowProps}
      variant={variant}
      loading={loading}
      title={title}
      subtitle={subtitleContent}
      avatar={
        loading ? (
          <Skeleton
            width={`${size}px`}
            height={`${size}px`}
            variant="circular"
          />
        ) : (
          <CachedUserAvatar
            userId={person.id}
            url={person.avatarUrl ?? null}
            name={displayName}
            size={size}
          />
        )
      }
    />
  );
}

/** A workspace Mate, shown like a group with the AI mark. */
export function MateRow(props: Omit<GroupRowProps, 'icon'>) {
  return <GroupRow {...props} icon={<AiIcon className={styles.groupIcon} />} />;
}

export function GroupRow({
  name,
  description,
  icon = <GroupIcon className={styles.groupIcon} />,
  showTooltips = false,
  variant = 'row',
  ...rowProps
}: GroupRowProps) {
  const title =
    showTooltips && typeof name === 'string' ? (
      <Tooltip content={name} rootOptions={{ delayDuration: 1000 }}>
        <span>{name}</span>
      </Tooltip>
    ) : (
      name
    );
  const subtitle =
    showTooltips && typeof description === 'string' ? (
      <Tooltip content={description} rootOptions={{ delayDuration: 1000 }}>
        <span>{description}</span>
      </Tooltip>
    ) : (
      description
    );

  return (
    <IdentityRow
      {...rowProps}
      variant={variant}
      title={title}
      subtitle={subtitle}
      icon={icon}
    />
  );
}
